/**
 * The decisions behind the admin cashback form, kept out of the .tsx so they
 * can be tested. The server is the authority on every rule here (its
 * `promotionProblems`, whose wording these messages copy); this only stops
 * obvious mistakes before a request and says what the terms mean in plain
 * words.
 */

import type { CashbackRunnerUp } from "src/services/cashback-api";
import {
  currencyTimeZone,
  isoToZonedWall,
  lastDayInstant,
  SHOP_TIME_ZONE,
  shortDayMonth,
  zonedWallToIso,
} from "src/utils/market-time";
import { CURRENCY_CONVERSION_TO_DEFAULT } from "@configs/currencies";
import { centsToUnitsInput } from "src/utils/admin-money";

export const CASHBACK_ADMIN_CURRENCIES = ["eur", "pln", "czk", "ron"] as const;
export type CashbackAdminCurrency = (typeof CASHBACK_ADMIN_CURRENCIES)[number];

/** What each currency row covers: EUR is every euro shop, Austria included. */
export const CURRENCY_ROW_LABELS: Record<CashbackAdminCurrency, string> = {
  eur: "EUR (SI, HR, SK, AT)",
  pln: "PLN (PL)",
  czk: "CZK (CZ)",
  ron: "RON (RO)",
};

/** The server's MAX_GRANT_CENTS (credit-math.ts): no lot may exceed it. */
export const MAX_GRANT_CENTS: Record<CashbackAdminCurrency, number> = {
  eur: 100_000,
  czk: 2_500_000,
  pln: 400_000,
  ron: 500_000,
};

/**
 * A promotion's platform. Promotions on different platforms may run at the
 * same time; one for both platforms ("ALL") overlaps every other. The
 * server's exclusion constraint is the authority.
 */
export const PROMOTION_CHANNELS = ["ALL", "WEB", "MOBILE"] as const;
export type PromotionChannel = (typeof PROMOTION_CHANNELS)[number];

export const CHANNEL_LABELS: Record<PromotionChannel, string> = {
  ALL: "Splet in aplikacija",
  WEB: "Samo splet",
  MOBILE: "Samo aplikacija",
};

export const CHANNEL_HELP =
  "Akciji samo za splet in samo za aplikacijo lahko tečeta hkrati. Akcija za splet in aplikacijo se ne sme prekrivati z nobeno drugo.";

/** A stored or sent value → a known channel; anything else (an older API) reads as ALL. */
export function toChannel(value: unknown): PromotionChannel {
  return (PROMOTION_CHANNELS as readonly unknown[]).includes(value)
    ? (value as PromotionChannel)
    : "ALL";
}

export function channelLabel(value: unknown): string {
  return CHANNEL_LABELS[toChannel(value)];
}

/** Where an order was placed (the award list): the order's own platform. */
export function orderChannelLabel(value: unknown): string {
  return value === "MOBILE" ? "Aplikacija" : value === "WEB" ? "Splet" : "—";
}

/**
 * The promotions an admin can still act on — every running one (one per
 * platform at most, or a single "ALL") and every scheduled one — running
 * first, then by start.
 */
export function livePromotions<T extends { startsAt: string; endsAt: string }>(
  promotions: T[],
  now: number,
): T[] {
  return promotions
    .filter((p) => Date.parse(p.endsAt) > now)
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

export type PromotionForm = {
  name: string;
  channel: PromotionChannel;
  ratePercent: string;
  lotLifetimeDays: string;
  /** `datetime-local` values, in the admin's own timezone. */
  startsAt: string;
  endsAt: string;
  currencies: Record<
    CashbackAdminCurrency,
    { on: boolean; minimum: string; cap: string }
  >;
};

export type CurrencyRule = { minimumCents: number; capCents: number | null };

export type PromotionPayload = {
  name: string;
  channel: PromotionChannel;
  rateBp: number;
  lotLifetimeDays: number;
  currencyRules: Partial<Record<CashbackAdminCurrency, CurrencyRule>>;
  startsAt: string;
  endsAt: string;
};

/** What the API returns for a promotion (dates as ISO strings over JSON). */
export type PromotionLike = {
  id: string;
  name: string;
  rateBp: number;
  lotLifetimeDays: number;
  currencyRules: Record<string, unknown>;
  startsAt: string | Date;
  endsAt: string | Date;
  channel?: string | null;
};

export function emptyForm(): PromotionForm {
  const off = () => ({ on: false, minimum: "", cap: "" });
  return {
    name: "",
    channel: "ALL",
    ratePercent: "",
    lotLifetimeDays: "90",
    startsAt: "",
    endsAt: "",
    currencies: { eur: off(), pln: off(), czk: off(), ron: off() },
  };
}

/**
 * The create form: every currency ticked (the promotion is meant for every
 * shop), the amounts still to fill in. `emptyForm` (all off) is the base a
 * saved promotion is read into.
 */
export function newPromotionForm(): PromotionForm {
  const f = emptyForm();
  for (const cur of CASHBACK_ADMIN_CURRENCIES) f.currencies[cur].on = true;
  return f;
}

/**
 * A EUR amount in another currency, rounded to what a shop would print:
 * whole złoty and lei, tens of koruna. "60" → PLN "255", CZK "1510", RON "300".
 * Null when the EUR amount is blank or unreadable.
 */
export function suggestFromEur(
  eurUnits: string,
  cur: CashbackAdminCurrency,
): string | null {
  const eurCents = hundredths(eurUnits);
  if (!Number.isInteger(eurCents) || eurCents <= 0) return null;
  if (cur === "eur") return eurUnits.trim();
  const rate =
    CURRENCY_CONVERSION_TO_DEFAULT[cur as keyof typeof CURRENCY_CONVERSION_TO_DEFAULT];
  const units = (eurCents / 100) * rate;
  const step = cur === "czk" ? 10 : 1;
  return String(Math.max(step, Math.round(units / step) * step));
}

/** Fill the blank minimum and cap of PLN/CZK/RON from the EUR row (typed values stay). */
export function fillFromEur(form: PromotionForm): PromotionForm {
  const eur = form.currencies.eur;
  const next = { ...form, currencies: { ...form.currencies } };
  for (const cur of CASHBACK_ADMIN_CURRENCIES) {
    if (cur === "eur") continue;
    const row = { ...next.currencies[cur] };
    if (!row.on) continue;
    if (row.minimum.trim() === "")
      row.minimum = suggestFromEur(eur.minimum, cur) ?? "";
    if (row.cap.trim() === "") row.cap = suggestFromEur(eur.cap, cur) ?? "";
    next.currencies[cur] = row;
  }
  return next;
}

// At most two decimals: "7,555" is refused, never silently rounded.
const DECIMAL = /^\s*\d+(?:[.,]\d{1,2})?\s*$/;

/** "7,5" → 750; "abc" or "7,555" → NaN. Used for both percent → bp and units → cents. */
function hundredths(v: string): number {
  if (!DECIMAL.test(v ?? "")) return NaN;
  return Math.round(parseFloat(v.replace(",", ".")) * 100);
}

/**
 * A form value as an ISO instant. A plain `datetime-local` wall time is read
 * as shop (Ljubljana) time, whatever the runtime's zone; a value that already
 * names its zone (`Z` or `±hh:mm`) is taken as the instant it states.
 */
function instant(local: string): string | null {
  if (!local) return null;
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(local)) {
    const d = new Date(local);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  }
  return zonedWallToIso(local, SHOP_TIME_ZONE);
}

/** An ISO instant → the `datetime-local` value in shop (Ljubljana) time. */
export function toLocalInput(value: string | Date): string {
  return isoToZonedWall(value, SHOP_TIME_ZONE);
}

function datesProblems(
  startsAt: string | null,
  endsAt: string | null,
): string[] {
  if (!startsAt || !endsAt) return ["Začetek in konec sta obvezna."];
  if (Date.parse(endsAt) <= Date.parse(startsAt))
    return ["Konec mora biti po začetku."];
  return [];
}

export function toPayload(form: PromotionForm): {
  payload: PromotionPayload | null;
  errors: string[];
} {
  const errors: string[] = [];
  const name = form.name.trim();
  if (!name) errors.push("Ime akcije je obvezno.");

  const rateBp = hundredths(form.ratePercent);
  if (!Number.isInteger(rateBp) || rateBp < 1 || rateBp > 5000)
    errors.push("Odstotek mora biti med 0,01 % in 50 %.");

  const days = /^\s*\d+\s*$/.test(form.lotLifetimeDays)
    ? Number(form.lotLifetimeDays)
    : NaN;
  if (!Number.isInteger(days) || days < 1 || days > 730)
    errors.push("Veljavnost dobroimetja mora biti od 1 do 730 dni.");

  const currencyRules: Partial<Record<CashbackAdminCurrency, CurrencyRule>> =
    {};
  for (const cur of CASHBACK_ADMIN_CURRENCIES) {
    const row = form.currencies[cur];
    if (!row?.on) continue;
    const minimumCents = hundredths(row.minimum);
    if (row.minimum.trim() === "")
      errors.push(
        `${cur.toUpperCase()}: vpišite najnižji znesek nakupa (ali odkljukajte valuto).`,
      );
    else if (!Number.isInteger(minimumCents) || minimumCents < 0)
      errors.push(`${cur.toUpperCase()}: najnižji znesek ni veljaven.`);
    let capCents: number | null = null;
    if (row.cap.trim() !== "") {
      capCents = hundredths(row.cap);
      const ceiling = MAX_GRANT_CENTS[cur];
      if (!Number.isInteger(capCents) || capCents <= 0 || capCents > ceiling)
        errors.push(
          `${cur.toUpperCase()}: največje vračilo mora biti med 0,01 in ${ceiling / 100}.`,
        );
    }
    currencyRules[cur] = { minimumCents, capCents };
  }
  if (Object.keys(currencyRules).length === 0)
    errors.push("Akcija mora veljati vsaj v eni valuti.");

  const startsAt = instant(form.startsAt);
  const endsAt = instant(form.endsAt);
  errors.push(...datesProblems(startsAt, endsAt));

  if (errors.length) return { payload: null, errors };
  return {
    payload: {
      name,
      channel: form.channel,
      rateBp,
      lotLifetimeDays: days,
      currencyRules,
      startsAt: startsAt!,
      endsAt: endsAt!,
    },
    errors,
  };
}

/**
 * A started promotion's terms are frozen (pre-flight M5): send only what may
 * still change, so a stale form can never trip the server's lock.
 */
export function toStartedPatch(form: PromotionForm): {
  payload: { name: string; endsAt: string };
  errors: string[];
} {
  const errors: string[] = [];
  const name = form.name.trim();
  if (!name) errors.push("Ime akcije je obvezno.");
  const endsAt = instant(form.endsAt);
  const startsAt = instant(form.startsAt);
  if (!endsAt) errors.push("Začetek in konec sta obvezna.");
  else if (startsAt && Date.parse(endsAt) <= Date.parse(startsAt))
    errors.push("Konec mora biti po začetku.");
  return { payload: { name, endsAt: endsAt ?? "" }, errors };
}

const units = (cents: number) => String(cents / 100).replace(".", ",");

export function fromPromotion(p: PromotionLike): PromotionForm {
  const form = emptyForm();
  form.name = p.name;
  form.channel = toChannel(p.channel);
  form.ratePercent = units(p.rateBp);
  form.lotLifetimeDays = String(p.lotLifetimeDays);
  form.startsAt = toLocalInput(p.startsAt);
  form.endsAt = toLocalInput(p.endsAt);
  for (const cur of CASHBACK_ADMIN_CURRENCIES) {
    const rule = p.currencyRules?.[cur] as
      { minimumCents?: number; capCents?: number | null } | null | undefined;
    if (!rule) continue;
    form.currencies[cur] = {
      on: true,
      minimum: centsToUnitsInput(Number(rule.minimumCents) || 0),
      cap: rule.capCents == null ? "" : centsToUnitsInput(rule.capCents),
    };
  }
  return form;
}

/** The backend's reward rule at exactly the minimum: floor, then cap, then ceiling. */
export function rewardAt(
  qualifyingCents: number,
  rateBp: number,
  capCents: number | null,
  currency: CashbackAdminCurrency,
): number {
  if (qualifyingCents <= 0) return 0;
  const raw = Math.floor((qualifyingCents * rateBp) / 10_000);
  return Math.max(
    0,
    Math.min(
      raw,
      capCents ?? Number.POSITIVE_INFINITY,
      MAX_GRANT_CENTS[currency],
    ),
  );
}

/** "Kupec, ki kupi za 60,00 €, dobi 6,00 € nazaj." — or null when the row says nothing yet. */
export function previewSentence(
  form: PromotionForm,
  currency: CashbackAdminCurrency,
  fmt: (cents: number) => string,
): string | null {
  const row = form.currencies[currency];
  if (!row?.on) return null;
  const rateBp = hundredths(form.ratePercent);
  const minimumCents = hundredths(row.minimum);
  if (
    !Number.isInteger(rateBp) ||
    rateBp < 1 ||
    !Number.isInteger(minimumCents) ||
    minimumCents <= 0
  )
    return null;
  const capRaw = row.cap.trim() === "" ? null : hundredths(row.cap);
  const capCents =
    capRaw !== null && Number.isInteger(capRaw) && capRaw > 0 ? capRaw : null;
  const reward = rewardAt(minimumCents, rateBp, capCents, currency);
  if (reward <= 0) return null;
  return `Kupec, ki kupi za ${fmt(minimumCents)}, dobi ${fmt(reward)} nazaj.`;
}

/**
 * The shop banner's two lines for one currency, worded exactly as the shop
 * words them in Slovenian (common.json cashback.bannerHeadline / …Detail,
 * via the same rules as `bannerCopy`), so the admin sees what customers will
 * read, cap included. Null while the row cannot be previewed yet.
 */
export const BANNER_SL = {
  headline: "Kupite za {{minimum}}, dobite {{reward}} nazaj.",
  headlineNoMinimum: "{{rate}} % nakupa dobite nazaj kot dobroimetje.",
  detail:
    "{{rate}} % vrednosti nakupa vam vrnemo kot dobroimetje za naslednji nakup. Akcija velja do {{end}}.",
  detailCapped:
    "{{rate}} % vrednosti nakupa vam vrnemo kot dobroimetje za naslednji nakup (največ {{cap}}). Akcija velja do {{end}}.",
} as const;

const fill = (template: string, params: Record<string, string>) =>
  template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => params[k] ?? "");

export function bannerPreview(
  form: PromotionForm,
  currency: CashbackAdminCurrency,
  fmt: (cents: number) => string,
): { headline: string; detail: string } | null {
  const row = form.currencies[currency];
  if (!row?.on) return null;
  const rateBp = hundredths(form.ratePercent);
  const minimumCents = hundredths(row.minimum);
  if (
    !Number.isInteger(rateBp) ||
    rateBp < 1 ||
    !Number.isInteger(minimumCents) ||
    minimumCents < 0
  )
    return null;
  const capRaw = row.cap.trim() === "" ? null : hundredths(row.cap);
  const capCents =
    capRaw !== null && Number.isInteger(capRaw) && capRaw > 0 ? capRaw : null;
  const rate = new Intl.NumberFormat("sl", { maximumFractionDigits: 2 }).format(
    rateBp / 100,
  );
  const iso = instant(form.endsAt);
  const end = iso
    ? shortDayMonth(lastDayInstant(iso), "sl", currencyTimeZone(currency), {
        endsSentence: true,
      })
    : "…";
  const headline =
    minimumCents > 0
      ? fill(BANNER_SL.headline, {
          minimum: fmt(minimumCents),
          reward: fmt(rewardAt(minimumCents, rateBp, capCents, currency)),
        })
      : fill(BANNER_SL.headlineNoMinimum, { rate });
  const detail =
    capCents != null
      ? fill(BANNER_SL.detailCapped, { rate, end, cap: fmt(capCents) })
      : fill(BANNER_SL.detail, { rate, end });
  return { headline, detail };
}

export type AwardChip = {
  label: string;
  color: "default" | "success" | "warning" | "error" | "info";
};

/** The status chip. "Zmanjšano" is a GRANTED award a refund has cut back. */
/**
 * What a PENDING award waits for. Without the order's payment option (the
 * awards list) it is the neutral "Čaka"; on the order page it is worded per
 * payment: cash on delivery waits for delivery, a card order for payment.
 */
export function pendingWording(paymentOption?: string | null): {
  label: string;
  explanation: string | null;
} {
  if (paymentOption === "PAYMENT_ON_DELIVERY")
    return {
      label: "Čaka na prevzem",
      explanation: "Naročilo po povzetju čaka na dostavo.",
    };
  if (paymentOption)
    return {
      label: "Čaka na plačilo",
      explanation: "Plačilo naročila še ni potrjeno.",
    };
  return { label: "Čaka", explanation: null };
}

export function awardStatusChip(
  a: { status: string; amountCents: number; entitledCents: number },
  paymentOption?: string | null,
): AwardChip {
  switch (a.status) {
    case "PENDING":
      return { label: pendingWording(paymentOption).label, color: "info" };
    case "GRANTED":
      return a.entitledCents < a.amountCents
        ? { label: "Zmanjšano", color: "warning" }
        : { label: "Podeljeno", color: "success" };
    case "CANCELED":
      return { label: "Preklicano", color: "default" };
    case "VOIDED":
      return { label: "Razveljavljeno", color: "error" };
    default:
      return { label: a.status, color: "default" };
  }
}

/** The sweep stops looking at a PENDING award this many days after it was created. */
export const PENDING_SWEEP_DAYS = 90;

/** Whole days since `createdAt`. */
export function ageDays(
  createdAt: string | Date,
  now: Date = new Date(),
): number {
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}

/**
 * "Čaka · 12 dni", and whether a person must look: a PENDING award older than
 * the sweep's window will never be settled automatically. `prefix` is the
 * pending wording (see `pendingWording`).
 */
export function pendingAgeLabel(
  createdAt: string | Date,
  now: Date = new Date(),
  prefix = "Čaka",
): { label: string; stale: boolean } {
  const days = ageDays(createdAt, now);
  // Slovenian dual and plural follow the last two digits (101 dan, 102 dneva).
  const d = days % 100;
  const unit =
    d === 1 ? "dan" : d === 2 ? "dneva" : d === 3 || d === 4 ? "dnevi" : "dni";
  return {
    label: `${prefix} · ${days} ${unit}`,
    stale: days > PENDING_SWEEP_DAYS,
  };
}

/** What a "Poskusi znova" / "Razveljavi" pass did, in the operator's words. */
export function settleOutcomeLabel(outcome: string): string {
  const labels: Record<string, string> = {
    none: "naročilo ne prejme vračila",
    waiting: "vračilo še čaka (naročilo še ni plačano oz. dostavljeno)",
    granted: "vračilo podeljeno",
    canceled: "vračilo preklicano",
    reduced: "vračilo zmanjšano",
    voided: "vračilo razveljavljeno",
    unchanged: "ni sprememb",
    retry: "ni uspelo, sistem bo poskusil znova (glej Napaka)",
  };
  return labels[outcome] ?? outcome;
}

/** How loudly to report an action's outcome: a retry needs a look, "unchanged" is news, the rest is success. */
export function outcomeSeverity(
  outcome: string,
): "success" | "info" | "warning" {
  if (outcome === "retry") return "warning";
  if (outcome === "unchanged") return "info";
  return "success";
}

/** "Kupon JESEN10" or "Akcija »Jesen 2026«": where an award came from. */
export function awardSourceLabel(a: {
  source?: string | null;
  couponCode?: string | null;
  promotionName?: string | null;
}): string {
  if (a.source === "COUPON") return `Kupon ${a.couponCode ?? ""}`.trim();
  return a.promotionName ? `Akcija »${a.promotionName}«` : "Akcija";
}

/** The candidate that lost under "larger wins", for support; null without one. */
export function runnerUpLine(
  a: { runnerUp?: CashbackRunnerUp | null },
  fmt: (cents: number) => string,
): string | null {
  const r = a.runnerUp;
  if (!r) return null;
  return r.source === "PROMOTION"
    ? `Akcija »${r.promotionName ?? "—"}« bi dala ${fmt(r.amountCents)} — uporabljen ugodnejši kupon.`
    : `Kupon ${r.couponCode} bi dal ${fmt(r.amountCents)} — uporabljena ugodnejša akcija.`;
}

/**
 * How the banner will word the end the admin typed (stage walk B3): the last
 * day that earns, read in each enabled currency's market zone. Only RON's
 * Bucharest can differ from Ljubljana (Warsaw keeps the same clock), so it is
 * named only when it does. Null while the end is blank or unreadable.
 *
 * "Kupci bodo videli: »Akcija velja do 6. 10.« Zadnji dan, ko nakup še
 * prinese vračilo."
 */
export function endDateHint(
  endsAtLocal: string,
  currencies: Partial<Record<CashbackAdminCurrency, { on: boolean }>>,
): string | null {
  const iso = instant(endsAtLocal);
  if (!iso) return null;
  const label = (cur: string) =>
    shortDayMonth(lastDayInstant(iso), "sl", currencyTimeZone(cur), {
      endsSentence: true,
    });
  const base = label("eur");
  const differing = CASHBACK_ADMIN_CURRENCIES.filter(
    (c) => currencies[c]?.on && label(c) !== base,
  ).map((c) => `${c.toUpperCase()}: ${label(c)}.`);
  const extra = differing.length
    ? ` (${differing.join(", ")}) Romunija je eno uro pred nami, zato je tam ob koncu akcije morda že naslednji dan.`
    : "";
  return `Kupci bodo videli: »Akcija velja do ${base}.«${extra} To je zadnji dan, ko nakup še prinese vračilo.`;
}
